import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import * as React from 'react';

import {PushNotificationFn} from 'app/components/Notification';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {isOwnerOrAdmin} from 'app/utils/userUtils';

import cs from './OrganizationView.module.scss';

interface OrganizationSettingDropdownProps<T extends string> {
  label: string;
  value: T;
  options: Record<string, string>;
  onChange: (orgId: string, value: T) => Promise<void>;
  organization: I.ImmutableOf<ApiOrganization>;
  profile: I.ImmutableOf<ApiOrganizationUser>;
  pushNotification: PushNotificationFn;
  supportLink?: string;
}

export const OrganizationSettingDropdown = <T extends string>({
  label,
  value,
  options,
  onChange,
  organization,
  profile,
  pushNotification,
  supportLink,
}: OrganizationSettingDropdownProps<T>) => {
  const [isUpdating, setIsUpdating] = React.useState(false);

  const organizationId = organization.get('id');
  const organizationName = organization.get('name');

  const canUpdate = isOwnerOrAdmin(profile.get('role'));

  const handleOnChange = React.useCallback(
    async (newValue: T) => {
      try {
        setIsUpdating(true);
        await onChange(organizationId, newValue);
        pushNotification({
          message: `${label} updated.`,
          autoHideDuration: 3000,
          options: {
            intent: B.Intent.SUCCESS,
          },
        });
      } catch (_e) {
        pushNotification({
          message: `There was a problem updating ${label}. Please try again.`,
          autoHideDuration: 3000,
          options: {
            intent: B.Intent.DANGER,
          },
        });
      } finally {
        setIsUpdating(false);
      }
    },
    [onChange, organizationId, pushNotification, label]
  );

  return (
    <div className={cs.field}>
      <label>
        {label}
        {supportLink && (
          <a
            className={cs.supportLinkIcon}
            href={supportLink}
            target="_blank"
            rel="noreferrer noopener"
          >
            <B.Icon icon="help" intent="none" />
          </a>
        )}
      </label>
      <B.Tooltip
        content={'Only organization admins can update this setting.'}
        disabled={canUpdate}
        className={cs.selectControl}
      >
        <B.HTMLSelect
          disabled={isUpdating || !canUpdate}
          fill
          value={value}
          onChange={(e) => handleOnChange(e.currentTarget.value as T)}
          aria-label={`Change ${label.toLowerCase()} for ${organizationName}`}
        >
          {Object.entries(options).map(([optionValue, displayName]) => (
            <option key={optionValue} value={optionValue}>
              {displayName}
            </option>
          ))}
        </B.HTMLSelect>
      </B.Tooltip>
    </div>
  );
};
